import { Component, h, Prop, Element, State, Event, EventEmitter, Watch, Method, AttachInternals } from '@stencil/core';

@Component({
  tag: 'spw-radio',
  styleUrl: 'spw-radio.scss',
  shadow: true,
  formAssociated: true,
})
export class SpwRadio {
  private inputElement: HTMLInputElement;

  @Element() el: HTMLSpwRadioElement;

  /** Indique si le bouton radio est désactivé */
  @Prop() disabled: boolean = false;
  /** Indique si le bouton radio est sélectionné (coché), mutable et reflété dans le DOM */
  @Prop({ mutable: true, reflect: true }) checked: boolean = false;
  /** Indique s'il y a une erreur visuelle associée au bouton radio */
  @Prop() error: boolean = false;
  /** Nom du bouton radio, utilisé pour le grouper avec d'autres boutons radio dans un formulaire */
  @Prop() name: string;
  /** Valeur associée au bouton radio */
  @Prop() value: string;

  @State() internalChecked: boolean;

  @AttachInternals() internals: ElementInternals;

  /** Événement déclenché lorsque l'état du bouton radio change (coché ou décoché) */
  @Event({ bubbles: true, composed: true }) spwChange: EventEmitter<{ checked: boolean; value: string }>;

  private handleRadio() {
    if (!this.disabled && !this.internalChecked) {
      this.setCheckedState(true);
      this.spwChange.emit({ checked: this.internalChecked, value: this.value });
      this.uncheckOthers();
    }
  }

  private uncheckOthers() {
    const form = this.el.closest('body');
    if (form) {
      const radios = Array.from(form.querySelectorAll(`spw-radio[name="${this.name}"]`)) as HTMLSpwRadioElement[];
      radios.forEach(radio => {
        if (radio !== this.el) {
          radio.setChecked(false);
        }
      });
    }
  }

  /** Méthode pour changer l'état de la case à cocher. */
  @Method()
  async setChecked(checked: boolean) {
    this.setCheckedState(checked);
  }

  @Watch('checked')
  checkedChanged(newValue: boolean) {
    this.setCheckedState(newValue);
  }

  private setCheckedState(checked: boolean) {
    this.internalChecked = checked;
    this.checked = checked;
    if (this.inputElement) {
      this.inputElement.checked = checked;
    }
    this.updateFormValue();
  }

  private updateFormValue() {
    if (this.internalChecked) {
      this.internals.setFormValue(this.value);
    } else {
      this.internals.setFormValue(null);
    }
  }

  private handleKeydown(event) {
    if (event.key === ' ' || event.key === 'Enter') {
      this.handleRadio();
      event.preventDefault();
    }
  }

  componentWillLoad() {
    this.internalChecked = this.checked;
  }

  componentDidLoad() {
    this.setCheckedState(this.internalChecked);
    this.el.closest('form')?.addEventListener('formdata', (e: FormDataEvent) => {
      if (this.internalChecked) {
        e.formData.set(this.name, this.value);
      }
    });
  }

  formResetCallback() {
    this.setCheckedState(this.checked);
  }

  private get elementClass(): { [className: string]: boolean } {
    return {
      'spw-radio__wrapper': true,
      'spw-radio__wrapper--disabled': this.disabled,
      'spw-radio__wrapper--checked': this.internalChecked,
      'spw-radio__wrapper--error': this.error,
    };
  }

  render() {
    const slot = this.el.innerHTML;
    const hasSlot = Boolean(slot);

    return (
      <div class="spw-radio">
        <input
          type="radio"
          ref={el => (this.inputElement = el)}
          checked={this.internalChecked}
          onChange={this.handleRadio.bind(this)}
          name={this.name}
          value={this.value}
          disabled={this.disabled}
          style={{ display: 'none' }}
        />
        {hasSlot && (
          <label class={`spw-radio__label ${this.disabled ? 'spw-radio__label--disabled' : ''}`} onClick={this.handleRadio.bind(this)}>
            <slot />
          </label>
        )}
        <div
          tabIndex={this.disabled ? -1 : 0}
          onClick={this.handleRadio.bind(this)}
          onKeyDown={this.handleKeydown.bind(this)}
          role="radio"
          aria-checked={this.internalChecked}
          aria-disabled={this.disabled}
          aria-labelledby="label"
          class={this.elementClass}
        >
          {this.internalChecked && <span class="spw-radio__circle"></span>}
        </div>
      </div>
    );
  }
}
